import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, User, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DocumentSignRecipientResponse } from '@/lib/api';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { useRecipients, useSetRecipients } from '../hooks';
import { useRecentRecipients } from '../hooks/useRecentRecipients';
import { DEFAULT_DOCUMENT_SIGN_SETTINGS, DOCUMENT_SIGN_ARCHE_ID } from '../settings';
import { getRecipientColor } from './recipientColors';
import { ContactPickerDialog } from './ContactPickerDialog';

interface DraftRecipient {
  /** Stable React key for the row's lifetime — never reassigned. */
  key: string;
  /** Server id once the row has been saved; null for a new, unsaved row. */
  recipientId: string | null;
  name: string;
  email: string;
  role: 'signer' | 'viewer' | 'approver';
}

interface RecipientsTableProps {
  envelopeId: string;
  /**
   * Holder this envelope belongs to. Required for the recent-recipients
   * autofill — the suggestion list is scoped to the envelope's workspace.
   * Omit only in tests or contexts where the autofill isn't needed (the
   * suggest menu falls back to owner + Add-contact in that case).
   */
  envelopeHolderId?: string;
  /**
   * `'sequential'` numbers each row so the owner can verify the signing
   * order before sending; `'parallel'` (or omitted) hides the numbers
   * since order is meaningless when everyone can sign at any time.
   */
  signingOrder?: 'parallel' | 'sequential';
  /** When true, hide all editing affordances. Used once the envelope is sent. */
  readOnly?: boolean;
}

/**
 * Imperative handle the envelope builder uses to flush pending recipient
 * edits when the user navigates (Back / Review and send). `save` returns
 * false when a row fails validation — the builder blocks navigation on a
 * false result and leaves the inline error visible.
 */
export interface RecipientsTableHandle {
  save: () => Promise<boolean>;
}

/** Identifies which row + field currently has the suggestion menu open. */
interface ActiveSuggest {
  index: number;
  field: 'name' | 'email';
}

const ROLE_LABEL: Record<DraftRecipient['role'], string> = {
  signer: 'Signer',
  viewer: 'Viewer',
  approver: 'Approver',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let scratchKeyCounter = 0;
function nextScratchKey(): string {
  scratchKeyCounter += 1;
  return `new-${Date.now()}-${scratchKeyCounter}`;
}

function toDraft(r: DocumentSignRecipientResponse): DraftRecipient {
  return {
    key: r.recipientId,
    recipientId: r.recipientId,
    name: r.name ?? '',
    email: r.email ?? '',
    role: r.role,
  };
}

export const RecipientsTable = forwardRef<RecipientsTableHandle, RecipientsTableProps>(
  function RecipientsTable({ envelopeId, envelopeHolderId, signingOrder, readOnly = false }, ref) {
    const isSequential = signingOrder === 'sequential';
    const { recipients: recentRecipients } = useRecentRecipients(envelopeHolderId ?? null);
    const { recipients, loading } = useRecipients(envelopeId);
    const setRecipients = useSetRecipients(envelopeId);
    const [dsSettings] = useArcheSettings(DOCUMENT_SIGN_ARCHE_ID, DEFAULT_DOCUMENT_SIGN_SETTINGS);

    const [drafts, setDrafts] = useState<DraftRecipient[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [activeSuggest, setActiveSuggest] = useState<ActiveSuggest | null>(null);
    const [contactPickerRow, setContactPickerRow] = useState<number | null>(null);
    const errorRef = useRef<HTMLParagraphElement>(null);
    const hydratedRef = useRef(false);
    const draftsRef = useRef(drafts);

    // Hydrate once from the server list. After that the component owns the
    // drafts — saves reconcile them from the mutation result, so the refetch
    // an auto-save triggers never clobbers in-progress edits.
    useEffect(() => {
      if (loading || hydratedRef.current) return;
      setDrafts(recipients.map(toDraft));
      hydratedRef.current = true;
    }, [recipients, loading]);

    // Latest drafts, readable inside async callbacks without a stale closure.
    useEffect(() => {
      draftsRef.current = drafts;
    }, [drafts]);

    // The save is triggered from the page's Back / Review buttons, which can
    // sit far from this section — pull a validation error into view so a
    // blocked navigation never looks like nothing happened.
    useEffect(() => {
      if (error) {
        errorRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      }
    }, [error]);

    // The owner can self-fill a row from their Document Sign settings —
    // offered whenever a default email is set and they aren't already among
    // the recipients. A blank display name is fine (the option still shows,
    // labelled by email); with no default email there is no suggestion.
    const ownerName = dsSettings.defaultDisplayName.trim();
    const ownerEmail = dsSettings.defaultEmail.trim().toLowerCase();
    const ownerIsRecipient =
      ownerEmail !== '' && drafts.some((d) => d.email.trim().toLowerCase() === ownerEmail);
    const ownerOption =
      ownerEmail && !ownerIsRecipient ? { name: ownerName, email: ownerEmail } : null;

    function update(index: number, patch: Partial<DraftRecipient>) {
      setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
      setIsDirty(true);
    }

    function remove(index: number) {
      setDrafts((prev) => prev.filter((_, i) => i !== index));
      setIsDirty(true);
    }

    function move(index: number, delta: -1 | 1) {
      setDrafts((prev) => {
        const target = index + delta;
        if (target < 0 || target >= prev.length) return prev;
        const next = [...prev];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
      setIsDirty(true);
    }

    function add() {
      setDrafts((prev) => [
        ...prev,
        { key: nextScratchKey(), recipientId: null, name: '', email: '', role: 'signer' },
      ]);
      setIsDirty(true);
    }

    function fillFromOwner(index: number) {
      update(index, { name: ownerName, email: ownerEmail });
      setActiveSuggest(null);
    }

    function fillFromRecent(index: number, recent: { name: string; email: string }) {
      update(index, { name: recent.name, email: recent.email });
      setActiveSuggest(null);
    }

    /**
     * Recent-recipient suggestions filtered against the current draft list
     * (no point offering someone who's already in the envelope). Computed
     * here so both Name and Email focus handlers offer the same set.
     */
    const draftEmails = new Set(
      drafts.map((d) => d.email.trim().toLowerCase()).filter((e) => e !== ''),
    );
    const recentSuggestions = recentRecipients.filter((r) => !draftEmails.has(r.email));

    function openContactPicker(index: number) {
      setActiveSuggest(null);
      setContactPickerRow(index);
    }

    /**
     * Flush pending edits. A `silent` background save skips the error surface
     * and never blocks; the navigation-triggered save (silent=false) shows
     * validation errors and returns false so the builder can block. Rows with
     * no input are dropped; every remaining row needs a name and a valid,
     * unique email. On success the rows are reconciled with their server ids.
     */
    const persist = useCallback(
      async (silent: boolean): Promise<boolean> => {
        if (!isDirty) return true;

        // A row with neither field touched is just an unused row — drop it.
        const entered = drafts.filter((d) => d.name.trim() !== '' || d.email.trim() !== '');
        const trimmed = entered.map((d) => ({
          ...d,
          name: d.name.trim(),
          email: d.email.trim().toLowerCase(),
        }));

        for (const d of trimmed) {
          if (!d.name) {
            if (!silent) setError('Each recipient needs a name.');
            return false;
          }
          if (!d.email || !EMAIL_RE.test(d.email)) {
            if (!silent) setError(`Invalid email: ${d.email || '(empty)'}`);
            return false;
          }
        }
        const seenEmails = new Set<string>();
        for (const d of trimmed) {
          if (seenEmails.has(d.email)) {
            if (!silent) setError(`Duplicate email: ${d.email}`);
            return false;
          }
          seenEmails.add(d.email);
        }

        const snapshot = draftsRef.current;
        try {
          const saved = await setRecipients.mutateAsync(
            trimmed.map((d, i) => ({
              // Existing rows send their id so the server updates them in
              // place rather than recreating them (which cascade-deletes the
              // fields placed against them).
              recipientId: d.recipientId ?? undefined,
              name: d.name,
              email: d.email,
              role: d.role,
              routingOrder: i,
            })),
          );
          setError(null);
          // Backfill server ids onto the saved rows (saved is in payload
          // order); the React keys stay stable so focused inputs don't remount.
          setDrafts((prev) => {
            let k = 0;
            return prev.map((d) => {
              if (d.name.trim() === '' && d.email.trim() === '') return d;
              const row = saved[k];
              k += 1;
              return row ? { ...d, recipientId: row.recipientId } : d;
            });
          });
          // Clear dirty only if nothing was edited during the in-flight save.
          if (draftsRef.current === snapshot) setIsDirty(false);
          return true;
        } catch (err) {
          if (!silent) {
            setError(err instanceof Error ? err.message : 'Failed to save recipients');
          }
          return false;
        }
      },
      [drafts, isDirty, setRecipients],
    );

    const save = useCallback(() => persist(false), [persist]);

    useImperativeHandle(ref, () => ({ save }), [save]);

    // A ref to the latest persist, so the debounce effect needn't depend on it
    // (the mutation object changes identity on every render).
    const persistRef = useRef(persist);
    useEffect(() => {
      persistRef.current = persist;
    }, [persist]);

    // Auto-save shortly after edits settle so later steps (field placement)
    // see recipients without waiting for navigation. Silent — validation
    // errors surface only on the navigation-triggered save.
    useEffect(() => {
      if (!isDirty) return;
      const timer = setTimeout(() => {
        void persistRef.current(true);
      }, 700);
      return () => clearTimeout(timer);
    }, [drafts, isDirty]);

    if (loading && drafts.length === 0) {
      return <p className="text-sm text-muted-foreground">Loading recipients…</p>;
    }

    return (
      <div className="space-y-3">
        {drafts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recipients yet.</p>
        ) : (
          <ul className="space-y-2">
            {drafts.map((draft, i) => {
              const color = getRecipientColor(i);
              return (
                <li
                  key={draft.key}
                  className="rounded-md border bg-card p-3 space-y-3 sm:space-y-0 sm:flex sm:items-end sm:gap-3"
                >
                  <span
                    className="hidden sm:block h-9 w-2 rounded-sm"
                    aria-hidden="true"
                    style={{ background: color.swatch }}
                  />
                  {/*
                   * Sequential-order badge. Surfaced only when "One at a
                   * time" is selected so the owner can verify the order
                   * matches their intent before clicking Send. Parallel
                   * envelopes skip this — order is meaningless there.
                   */}
                  {isSequential && (
                    <span
                      className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                      aria-label={`Sign order ${i + 1}`}
                      title="Sign order"
                    >
                      {i + 1}
                    </span>
                  )}
                  <div className="flex-1 space-y-1">
                    <Label htmlFor={`recipient-name-${draft.key}`} className="text-xs">
                      Name
                    </Label>
                    <div className="relative">
                      <Input
                        id={`recipient-name-${draft.key}`}
                        value={draft.name}
                        maxLength={300}
                        onChange={(e) => update(i, { name: e.target.value })}
                        onFocus={() => setActiveSuggest({ index: i, field: 'name' })}
                        onBlur={() => setActiveSuggest(null)}
                        disabled={readOnly}
                      />
                      {activeSuggest?.index === i &&
                        activeSuggest.field === 'name' &&
                        draft.name.trim() === '' && (
                          <RecipientSuggestMenu
                            owner={ownerOption}
                            recent={recentSuggestions}
                            onPickOwner={() => fillFromOwner(i)}
                            onPickRecent={(r) => fillFromRecent(i, r)}
                            onAddContact={() => openContactPicker(i)}
                          />
                        )}
                    </div>
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label htmlFor={`recipient-email-${draft.key}`} className="text-xs">
                      Email
                    </Label>
                    <div className="relative">
                      <Input
                        id={`recipient-email-${draft.key}`}
                        type="email"
                        value={draft.email}
                        maxLength={500}
                        onChange={(e) => update(i, { email: e.target.value })}
                        onFocus={() => setActiveSuggest({ index: i, field: 'email' })}
                        onBlur={() => setActiveSuggest(null)}
                        disabled={readOnly}
                      />
                      {activeSuggest?.index === i &&
                        activeSuggest.field === 'email' &&
                        draft.email.trim() === '' && (
                          <RecipientSuggestMenu
                            owner={ownerOption}
                            recent={recentSuggestions}
                            onPickOwner={() => fillFromOwner(i)}
                            onPickRecent={(r) => fillFromRecent(i, r)}
                            onAddContact={() => openContactPicker(i)}
                          />
                        )}
                    </div>
                  </div>
                  <div className="w-full sm:w-32 space-y-1">
                    <Label htmlFor={`recipient-role-${draft.key}`} className="text-xs">
                      Role
                    </Label>
                    <Select
                      value={draft.role}
                      onValueChange={(value) =>
                        update(i, { role: value as DraftRecipient['role'] })
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger id={`recipient-role-${draft.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(['signer', 'viewer', 'approver'] as const).map((role) => (
                          <SelectItem key={role} value={role}>
                            {ROLE_LABEL[role]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {!readOnly && (
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        aria-label="Move up"
                      >
                        <ChevronUp className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => move(i, 1)}
                        disabled={i === drafts.length - 1}
                        aria-label="Move down"
                      >
                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(i)}
                        aria-label="Remove recipient"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {error && (
          <p ref={errorRef} className="text-sm text-destructive">
            {error}
          </p>
        )}

        {!readOnly && (
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
            Add recipient
          </Button>
        )}

        <ContactPickerDialog
          open={contactPickerRow !== null}
          onOpenChange={(next) => {
            if (!next) setContactPickerRow(null);
          }}
          onPick={(picked) => {
            if (contactPickerRow !== null) {
              update(contactPickerRow, { name: picked.name, email: picked.email });
            }
          }}
        />
      </div>
    );
  },
);

// ---------------------------------------------------------------------------
// Field suggestion menu
// ---------------------------------------------------------------------------

interface RecipientSuggestMenuProps {
  /** The owner self-fill entry, or null when the owner is already a recipient. */
  owner: { name: string; email: string } | null;
  /**
   * Distinct recipients the workspace has previously sent envelopes to,
   * most-recently-used first, already filtered against the current draft
   * list. May be empty (e.g. workspace's first envelope, or every prior
   * recipient is already added).
   */
  recent: ReadonlyArray<{ name: string; email: string }>;
  onPickOwner: () => void;
  onPickRecent: (recent: { name: string; email: string }) => void;
  onAddContact: () => void;
}

/**
 * Dropdown shown under an empty Name/Email field. `onMouseDown` is suppressed
 * so clicking an entry doesn't blur the field before the click registers.
 *
 * Order: owner self-fill (if applicable), recent-recipient history (capped),
 * Add Contact. Recent history sits between the always-available actions so
 * an obvious self-fill stays at the top and "Add Contact" stays anchored at
 * the bottom where users expect a CTA.
 */
function RecipientSuggestMenu({
  owner,
  recent,
  onPickOwner,
  onPickRecent,
  onAddContact,
}: RecipientSuggestMenuProps) {
  // Cap the recent list so the dropdown stays scannable. Anything beyond
  // ~5 entries pushes Add Contact off the visible viewport on smaller
  // builder windows; the backend caps at 20 and we just take the first
  // few here.
  const visibleRecent = recent.slice(0, 5);
  return (
    <ul
      onMouseDown={(e) => e.preventDefault()}
      className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-md border bg-popover py-1 shadow-md"
    >
      {owner && (
        <li>
          <button
            type="button"
            onClick={onPickOwner}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
          >
            <User className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 truncate">
              {owner.name ? (
                <>
                  <span className="font-medium text-foreground">{owner.name}</span>
                  <span className="text-muted-foreground">: {owner.email}</span>
                </>
              ) : (
                <span className="font-medium text-foreground">{owner.email}</span>
              )}
            </span>
          </button>
        </li>
      )}
      {visibleRecent.length > 0 && (
        <>
          {(owner || visibleRecent.length > 0) && (
            <li className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              Recently used
            </li>
          )}
          {visibleRecent.map((r) => (
            <li key={r.email}>
              <button
                type="button"
                onClick={() => onPickRecent(r)}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
              >
                <User className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 truncate">
                  <span className="font-medium text-foreground">{r.name}</span>
                  <span className="text-muted-foreground">: {r.email}</span>
                </span>
              </button>
            </li>
          ))}
        </>
      )}
      <li>
        <button
          type="button"
          onClick={onAddContact}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent"
        >
          <UserPlus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          Add Contact
        </button>
      </li>
    </ul>
  );
}
